import React, { useState, useMemo } from "react";
import {
  Box,
  IconButton,
  Typography,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import MDButton from "components/MDButton";
import { OverlayView } from "@react-google-maps/api";

dayjs.extend(utc);

const QUICK_RANGES = [
  { label: "Last 30 minute", value: 0.5 },
  { label: "Last 1 hour", value: 1 },
  { label: "Last 2 hours", value: 2 },
  { label: "Last 3 hours", value: 3 },
  { label: "Last 5 hours", value: 5 },
  { label: "Last 8 hours", value: 8 },
];

export default function UserRoute({
  open,
  anchorPos,
  onClose,
  users = [],
  handleSelectUser,
}) {
  const [selectedUser, setSelectedUser] = useState(users);
  const [fromDate, setFromDate] = useState(dayjs().subtract(30, "minute"));
  const [toDate, setToDate] = useState(dayjs());
  const [quickRange, setQuickRange] = useState("");

  const position = useMemo(
    () => ({
      lat: parseFloat(users.geoX.trim()),
      lng: parseFloat(users.geoY.trim()),
    }),
    [users.geoX, users.geoY]
  );
  const handleQuickRangeChange = (e) => {
    const hrs = Number(e.target.value);
    setQuickRange(hrs);
    const now = dayjs();
    setToDate(now);
    setFromDate(now.subtract(hrs, "hour"));
  };

  const onDateSubmit = () => {
    const fromEpoch = fromDate.utc().unix();
    const toEpoch = toDate.utc().unix();

    handleSelectUser({
      user: selectedUser,
      from: fromEpoch,
      to: toEpoch,
    });
    setQuickRange("");
    onClose();
  };

  if (!open && !anchorPos) return null;

  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <Box
        sx={{
          background:
            "linear-gradient(150deg, rgba(255,255,255,0.97), rgba(243,248,255,0.95))",
          p: 0.45,
          borderRadius: 1.5,
          border: "1px solid rgba(37, 99, 235, 0.18)",
          boxShadow: "0 10px 20px rgba(15, 23, 42, 0.14)",
          width: 252,
          textAlign: "left",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 0.55,
            borderBottom: "1px solid rgba(148, 163, 184, 0.34)",
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, fontSize: "0.68rem", color: "#0f172a" }}
          >
            {selectedUser ? selectedUser.name : "Select a user to trace path"}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "#000",
              width: 18,
              height: 18,
              p: 0,
              border: "1px solid rgba(239, 68, 68, 0.22)",
              backgroundColor: "rgba(255, 255, 255, 0.92)",
            }}
          >
            <CloseIcon sx={{ fontSize: 12 }} color="error" />
          </IconButton>
        </Box>

        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.85,
              p: 0.8,
            }}
          >
            <TextField
              select
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  minHeight: 34,
                  borderRadius: 1,
                  fontSize: "0.72rem",
                },
                "& .MuiOutlinedInput-input": {
                  py: 0.7,
                  px: 1.1,
                  color: "#000",
                },
                "& .MuiSelect-select": {
                  minHeight: "34px !important",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputBase-input": {
                  color: "#000",
                },
                "& .MuiSvgIcon-root": {
                  color: "#000",
                  fontSize: 18,
                },
              }}
              variant="outlined"
              size="small"
              value={quickRange || ""}
              onChange={handleQuickRangeChange}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected)
                    return (
                      <span style={{ color: "#334155", fontSize: "0.72rem" }}>
                        Quick Range
                      </span>
                    );
                  return (
                    QUICK_RANGES.find((r) => r.value === selected)?.label ??
                    selected
                  );
                },
              }}
            >
              <MenuItem value="">
                <span>Quick Range</span>
              </MenuItem>
              {QUICK_RANGES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="datetime-local"
              InputLabelProps={{
                shrink: true,
                style: { color: "#000", fontSize: "0.62rem" },
              }}
              value={fromDate.format("YYYY-MM-DDTHH:mm")}
              onChange={(e) => {
                const newFrom = dayjs(e.target.value);
                setFromDate(newFrom);
                if (newFrom.isAfter(toDate)) {
                  setToDate(newFrom.add(30, "minute"));
                }
                setQuickRange("");
              }}
              sx={{
                "& .MuiInputBase-root": {
                  minHeight: 28,
                  borderRadius: 0.85,
                  fontSize: "0.66rem",
                },
                "& .MuiOutlinedInput-input": {
                  py: 0.42,
                  px: 0.8,
                  color: "#000",
                },
                "& input[type='datetime-local']::-webkit-calendar-picker-indicator": {
                  transform: "scale(0.78)",
                  opacity: 0.85,
                },
              }}
              inputProps={{ style: { color: "#000", fontSize: "0.66rem" } }}
            />

            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="datetime-local"
              InputLabelProps={{
                shrink: true,
                style: { color: "#000", fontSize: "0.62rem" },
              }}
              value={toDate.format("YYYY-MM-DDTHH:mm")}
              onChange={(e) => {
                setToDate(dayjs(e.target.value));
                setQuickRange("");
              }}
              error={toDate.isBefore(fromDate)}
              helperText={
                toDate.isBefore(fromDate)
                  ? "To Date must be after From Date"
                  : ""
              }
              sx={{
                "& .MuiInputBase-root": {
                  minHeight: 28,
                  borderRadius: 0.85,
                  fontSize: "0.66rem",
                },
                "& .MuiOutlinedInput-input": {
                  py: 0.42,
                  px: 0.8,
                  color: "#000",
                },
                "& input[type='datetime-local']::-webkit-calendar-picker-indicator": {
                  transform: "scale(0.78)",
                  opacity: 0.85,
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "0.58rem",
                  lineHeight: 1.15,
                  mt: 0.25,
                  ml: 0.2,
                },
              }}
              inputProps={{
                style: { color: "#000", fontSize: "0.66rem" },
                min: fromDate.format("YYYY-MM-DDTHH:mm"),
              }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.6 }}>
              <MDButton
                size="small"
                variant="contained"
                color="secondary"
                onClick={() => onClose()}
                sx={{
                  width: { xs: "100%", md: 96 },
                  minWidth: { xs: "100%", md: 96 },
                  height: 30,
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: "none",
                  borderRadius: 1,
                  borderColor: "#d1d5db",
                  px: 0.8,
                }}
              >
                Back
              </MDButton>
              <MDButton
                size="small"
                variant="contained"
                disabled={toDate.isBefore(fromDate)}
                onClick={onDateSubmit}
                color="success"
                sx={{
                  width: { xs: "100%", md: 96 },
                  minWidth: { xs: "100%", md: 96 },
                  height: 30,
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: "none",
                  borderRadius: 1,
                  borderColor: "#d1d5db",
                  px: 0.8,
                }}
              >
                Submit
              </MDButton>
            </Box>
          </Box>
        </>
      </Box>
    </OverlayView>
  );
}
